// Authoritative content sourced from "First Edition v0.1 — August 2026".
// Meaning is unchanged from source; only reading-order presentation (headings,
// list formatting, table structure) has been adjusted for HTML publication.
//
// EDITORIAL RESTRUCTURE (accessibility pass): the report is now organised as a
// concise CORE_SECTIONS narrative (Millism → Proportional Change → Rooms first →
// Alison/Number 2 → Target Operating Model → Website & Network → Roadmap →
// Resilience & Training → Five-Year Cost → North Star/Dynamic Profile →
// Recommendations → Closing), with supporting detail moved into labelled
// APPENDICES (A–H). No recommendation, figure, or conclusion was added, removed
// or reinterpreted: material was relocated, and prose was shortened for
// non-technical readers. RECOMMENDATIONS and CLOSING_BLOCKS are unchanged.

export const META = {
  title: "The Mill",
  subtitle: "Operating Model & Digital Strategy Report",
  edition: "First Edition v0.1",
  date: "August 2026",
  provenance:
    "Prepared from The Mill discovery and working strategy material. Content is authoritative and unchanged in substance from the approved source.",
  copyright: "\u00a9 2026 KATLAS Technology. All rights reserved.",
};

export const FRAMING =
  "The Mill does not primarily have a CRM problem. It has a collection of operational journeys: rooms, volunteers, activities, communications, partners, evidence, grants and finance, that have grown across separate tools, spreadsheets, folders, forms and individual working practices.";

// ---------------------------------------------------------------------------
// Executive Introduction — unchanged in substance from the prior Executive
// Summary; only the heading label changed, to match the plain-English section
// list agreed for the core report.
// ---------------------------------------------------------------------------
export const EXEC_SUMMARY = {
  id: "executive-summary",
  label: "Executive Introduction",
  blocks: [
    {
      type: "p",
      text: "The Mill does not primarily have a CRM problem. It has a collection of operational journeys: rooms, volunteers, activities, communications, partners, evidence, grants and finance, that have grown across separate tools, spreadsheets, folders, forms and individual working practices. The discovery identifies fragmented records, manual rekeying and hand-offs, disconnected room booking, fragmented communications, unclear decision rights and limited visibility of participation and outcomes as recurring problems.",
    },
    {
      type: "quote",
      text: "The strongest conclusion of the earlier analysis is therefore that the issue is not the absence of a conventional CRM, but the absence of joined-up journeys, reliable ownership and a proportionate operational record.",
    },
    {
      type: "p",
      text: "The proposed response is deliberately incremental. The Mill should use inexpensive specialist services for activities they already perform well, simplify the public web experience, establish distributed ownership and authority, measure whether administration actually falls, and retire legacy processes only after replacements have proved themselves.",
    },
    {
      type: "p",
      text: "The current working technology hypothesis is:",
    },
    {
      type: "list",
      variant: "grid",
      items: [
        "LemonBooking: rooms, events, booking, deposits/payments and potentially the public website.",
        "Plinth: volunteers, Network Organisations, community activity and impact.",
        "membermojo: organisation-owned membership where Network Organisations require it.",
        "Mailchimp: the authoritative Mill marketing audience and campaign service.",
        "Finance/banking: the authoritative financial record.",
        "Google Shared Drive: controlled documents and supporting evidence.",
        "Notion: internal knowledge and lightweight coordination where useful.",
        "Zapier: limited and documented integration glue.",
      ],
    },
    {
      type: "p",
      text: "This is a working architecture, not a commitment to implement every product or every capability.",
    },
    {
      type: "p",
      text: "The first proof should be rooms because room hire combines the website, customer experience, availability, policy, pricing, approval, payment, finance, access, communications and evidence in one bounded journey. The ambition is that a well-defined room should increasingly be capable of semi-booking itself: matching a customer's requirements against its profile, applying predetermined policies and pricing, taking a deposit, identifying exceptions and escalating only those decisions requiring human judgement.",
    },
    {
      type: "p",
      text: "Once that operating pattern has proved itself, the same principles can be extended to volunteers, Network Organisations, membership, participation and evidence. The objective is not digital transformation for its own sake. The Mill should be able to serve more people, organisations and activities, use its assets more effectively, increase earned income and produce better evidence without administration increasing at the same rate.",
    },
  ],
};

// ---------------------------------------------------------------------------
// CORE REPORT — ten sections. Each section carries a one-line "why" (plain-
// English signposting) rendered directly under its heading. Detail moved to
// an appendix is flagged with an "apxref" block, a plain paragraph linking to
// the relevant appendix.
// ---------------------------------------------------------------------------
export const SECTIONS = [
  {
    id: "millism",
    number: 1,
    title: "Millism: The Design Constraint",
    why: "Millism describes what The Mill must protect while it modernises: the human, informal, community character that makes it The Mill.",
    blocks: [
      { type: "p", text: "Digitisation must preserve Millism. That means retaining:" },
      {
        type: "list",
        variant: "flow",
        items: [
          "Human judgement",
          "Informality",
          "Accessibility",
          "Community initiative",
          "Volunteer contribution",
          "Assisted and non-digital routes where required",
          "Proportionate information collection",
          "The ability to deal sensibly with exceptions",
        ],
      },
      {
        type: "p",
        text: "The purpose of automation is not to turn The Mill into a corporate service centre. It is to remove hidden administration so people can spend more time doing the things that require people.",
      },
      {
        type: "p",
        text: "The discovery identifies low digital confidence and change fatigue as significant implementation risks, and recommends co-design, bounded pilots, assisted routes, minimal training and measuring whether administration genuinely decreases.",
      },
      {
        type: "quote",
        text: "Training lite, task focused and role specific. Nobody should need to \u201clearn the CRM\u201d. They should learn the small number of tasks required for their role.",
      },
    ],
  },
  {
    id: "proportional-change",
    number: 2,
    title: "Proportional Change: Where We Will, and Will Not, Intervene",
    why: "Not every existing process needs to change. This section sets out the test for deciding what is, and is not, in scope, and lists what the programme is deliberately not doing.",
    blocks: [
      { type: "p", text: "This programme does not assume that every existing process needs to change." },
      {
        type: "p",
        text: "A process should only become part of the change programme where there is reasonable evidence that doing so will materially improve one or more of:",
      },
      {
        type: "list",
        variant: "grid",
        items: [
          "The experience of residents, volunteers, staff, hirers or Network Organisations",
          "Administrative efficiency",
          "Operating cost",
          "Income or utilisation",
          "Resilience and continuity",
          "Privacy, control or accountability",
          "The quality and usefulness of evidence",
          "The ability of The Mill to grow without administration increasing proportionately",
        ],
      },
      {
        type: "p",
        text: "Processes that are specialist, already functioning adequately, affect only a small number of users, or would create more disruption than benefit, should normally remain unchanged. This is particularly relevant to funding applications, grant governance, accounting and other specialist processes; the programme may improve the interfaces around them, for example by making operational evidence easier to obtain, without redesigning the underlying process.",
      },
      { type: "p", text: "The default position is therefore:" },
      { type: "quote", text: "Do not change a process merely because technology makes change possible." },
      { type: "p", text: "A proposed change should be able to answer:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "What problem are we solving?",
          "Who materially benefits?",
          "What administrative effort or cost will fall?",
          "What measurable improvement should result?",
        ],
      },
      { type: "p", text: "If those questions cannot be answered convincingly, the process should remain outside scope." },
      {
        type: "p",
        text: "The exception is where trustees, governance reviewers, regulators or accountable operational owners identify a material risk, control weakness or organisational requirement that justifies intervention, even where the benefit is not universal.",
      },
      { type: "subhead", text: "What This Programme Is Deliberately Not Doing" },
      { type: "p", text: "Applying this test, the programme does not currently propose:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Procuring a large enterprise CRM",
          "Building a bespoke CommunityHub platform",
          "Integrating every application immediately",
          "Migrating every existing record",
          "Centralising all participant/member data at The Mill",
          "Automating human community judgement",
          "Collecting demographics because technology makes it possible",
          "Replacing the accounting system",
          "Buying dedicated technology for every activity",
          "Creating new central Forms/Data/Policy administrators",
          "Requiring every volunteer to become a sophisticated systems user",
          "Introducing AI before the underlying operational rules are understood",
        ],
      },
      {
        type: "quote",
        text: "These are not omissions. They are deliberate design boundaries intended to control cost, disruption, privacy risk and administrative complexity.",
      },
      { type: "p", text: "The programme's governing principle is therefore:" },
      {
        type: "quote",
        text: "Change where the benefit is material and sufficiently broad. Connect where an existing specialist process already works. Intervene where governance or risk requires it. Otherwise, leave it alone.",
      },
    ],
  },
  {
    id: "rooms-first-proof",
    number: 3,
    title: "Rooms: The First Proof",
    why: "Rooms are where the new approach is proved first, because they combine income, community use and every operational step in one place.",
    blocks: [
      {
        type: "p",
        text: "Rooms provide the best first demonstrator because they are both a community asset and an income-producing asset; the current Mill strategy already describes the building as a community asset, income opportunity and safety responsibility, and the room journey follows a natural sequence of offer, request, authorisation, accounting and learning.",
      },
      {
        type: "p",
        text: "Each room should develop an authoritative profile, a single, trusted description of that room, covering:",
      },
      {
        type: "list",
        variant: "flow",
        items: [
          "Dimensions and capacity",
          "Accessibility",
          "Facilities",
          "Furniture",
          "Equipment",
          "Permitted activities",
          "Support requirements",
          "Availability",
          "Set-up and clear-down",
          "Pricing",
          "Concessions",
          "Deposits",
          "Policies and terms",
        ],
      },
      {
        type: "p",
        text: "The customer's request describes: organisation; purpose; date/time; attendance; accessibility; equipment; services; pricing/entitlement status. The system can therefore increasingly determine:",
      },
      { type: "flowchain", steps: ["Suitable", "Suitable with conditions", "Alternative room", "Human review", "Unsuitable"] },
      {
        type: "quote",
        text: "The desired near-term outcome is not uncontrolled AI. It is bounded self-service: the system handles routine, predictable requests on its own, within limits The Mill sets, and refers anything unusual to a person. Routine bookings proceed automatically or semi-automatically where predetermined conditions are met. Human judgement remains for exceptions, concessions, policy issues or community priorities.",
      },
      { type: "p", text: "This is what is meant by the room increasingly \u201cbooking itself.\u201d" },
      { type: "p", text: "Once the room journey proves this pattern, it can be reused across the organisation:" },
      { type: "flowchain", steps: ["Profile", "Request", "Conditions", "Authority", "Action", "Evidence"] },
    ],
  },
  {
    id: "alison-number-2",
    number: 4,
    title: "Alison and the Number 2 Test: A One-Page Worked Example",
    why: "This single example shows, in practice, what the whole resilience approach means: reducing dependence on any one person, without criticising anyone.",
    blocks: [
      {
        type: "p",
        text: "Alison runs a small class of approximately 12 places. Despite the small capacity, she maintains a substantial spreadsheet for recruitment and participation, and receives repeated emails from parents asking routine questions. The purpose of this example is not to criticise Alison or the existing process. It is to show how a capable, committed person can gradually become:",
      },
      { type: "flowchain", steps: ["Group leader", "Operational record", "Knowledge base", "Helpdesk"], connector: "+" },
      {
        type: "p",
        text: "Routine questions may include dates and times, eligibility, price, accessibility, what to bring and waiting-list status, and the answers can end up spread across Alison's spreadsheet, emails, web pages, forms and her own memory.",
      },
      { type: "quote", text: "What happens if Alison is suddenly unavailable?" },
      {
        type: "p",
        text: "A suitably authorised deputy, or \u201cNumber 2\u201d, should not need Alison's password, personal spreadsheet, historic emails or memory to keep the class operating.",
      },
      {
        type: "p",
        text: "The first step is to publish the answers to routine questions once, on the class's own web page, rather than answering the same question repeatedly by email. Routine, predictable communications can then be generated automatically, while anything unusual still reaches a person.",
      },
      { type: "quote", text: "Publish the answer once rather than repeatedly answering the same question privately." },
      { type: "subhead", text: "The Number 2 Test" },
      {
        type: "p",
        text: "If Alison becomes unavailable, a nominated deputy should be able to use their own authorised access and quickly understand current capacity, confirmed participants, the waiting list and outstanding exceptions, without contacting Alison directly.",
      },
      { type: "flowchain", steps: ["Login", "Understand current state", "See exceptions", "Act within authority", "Continue service"] },
      { type: "quote", text: "Can a suitably authorised Number 2 take over without the Number 1 being available?" },
      {
        type: "p",
        text: "This becomes an acceptance principle for other important journeys too, from rooms to volunteers to communications. It does not mean duplicate administration merely to provide backup. The objective is:",
      },
      { type: "quote", text: "Individual ownership without organisational dependency." },
      { type: "apxref", text: "Appendix C sets out the full continuity checklist behind this example.", appendixId: "appendix-c" },
    ],
  },
  {
    id: "target-operating-model",
    number: 5,
    title: "Target Operating Model",
    why: "Before looking at individual changes, it helps to see the shape of the target model: fewer places for information to live, and clearer ownership of each one.",
    blocks: [
      {
        type: "p",
        text: "The Mill already possesses considerable digital capability. The problem is that capability has accumulated rather than been deliberately designed as one operating environment.",
      },
      { type: "p", text: "Current information and activity are distributed across combinations of:" },
      {
        type: "list",
        variant: "flow",
        items: [
          "WordPress",
          "Google personal and shared drives",
          "Google Forms",
          "Spreadsheets",
          "Email",
          "Mailchimp",
          "WhatsApp",
          "Notion",
          "Zapier",
          "Finance and banking systems",
          "Local documents",
          "Specialist services",
          "Individual knowledge",
        ],
      },
      {
        type: "p",
        text: "This is found across volunteer, room, finance, mailing and project information alike, resulting in repeated searching, inconsistent records and weak handovers. Room hire illustrates the problem particularly clearly: availability, enquiry, approval, deposits and invoicing currently involve disconnected, manually coordinated processes. Communications show a similar pattern: overlapping mailing audiences, Gmail groups, WhatsApp and individual knowledge can make it difficult to know who should receive what and who owns a relationship.",
      },
      {
        type: "quote",
        text: "The problem is therefore less: \u201cWhich CRM should we buy?\u201d and more: \u201cHow do we make The Mill easier to operate without losing what makes it The Mill?\u201d",
      },
      {
        type: "p",
        text: "The Mill needs to move from an environment organised around applications and files towards one organised around journeys, responsibilities and authoritative sources, the one agreed place each piece of information officially lives.",
      },
      {
        type: "quote",
        text: "A simple target principle is: one thing should have one clear home, one accountable owner and understood authority for consequential change.",
      },
      {
        type: "p",
        text: "This does not mean putting everything into one database; that would create real risks of complexity, inappropriate visibility and administrative burden. The architecture instead stays distributed: bookings belong in the booking system, members of a Network Organisation stay in that organisation's own membership service, finance stays in finance, policies stay in controlled documents, volunteers and impact may sit in Plinth, and public information is published through the website. Information only moves to another system where another role genuinely needs it.",
      },
      {
        type: "p",
        text: "This reflects the emerging governance principle: named ownership of the individual thing, defined custody of the information, and explicit authority for consequential change.",
      },
      { type: "apxref", text: "Appendix A sets out the full Change Matrix, covering all thirteen operational areas with their current problems, proposed changes, benefits, risks and success measures. Appendix B sets out the fuller governance rationale.", appendixId: "appendix-a" },
    ],
  },
  {
    id: "website-network-strategy",
    number: 6,
    title: "Website and Network Strategy",
    why: "The public website and the specialist systems behind it are one service, not separate projects; and Network Organisations keep responsibility for their own people.",
    blocks: [
      {
        type: "p",
        text: "The Mill should not regard the website and CRM as separate budget silos; they are components of the same service environment. The website should become the simple public front door for rooms, What's On, Network Organisations, activities, volunteering, joining links, policies, community opportunities, fundraising, art and events, and enquiries. The public should not need to know whether the service behind that page is LemonBooking, Plinth or membermojo.",
      },
      {
        type: "quote",
        text: "Website publishes. Applications transact. Mailchimp markets. Custodial systems, the specialist applications that hold the current, trusted record for their area, retain the authoritative record.",
      },
      { type: "p", text: "Google Drive can then progressively return to being a document repository instead of an accidental operational database." },
      {
        type: "p",
        text: "Network Organisations retain their own relationship with their members: The Mill manages The Mill's relationship with the Network Organisation, while the organisation manages its own relationship with its members. A participant may also remain simply a visitor without persistent membership. This avoids turning every community participant into a Mill CRM record.",
      },
      { type: "subhead", text: "Volunteer and Network Organisation Journeys" },
      {
        type: "p",
        text: "Once the room pattern of profile, request, conditions, authority, action and evidence has proved itself, it extends naturally to people and partners:",
      },
      { type: "flowchain", steps: ["Interest", "Conversation", "Appropriate information/checks", "Induction", "Role", "Support", "Participation", "Feedback", "Change/exit"] },
      { type: "flowchain", steps: ["Enquiry", "Organisation profile", "Appropriate verification", "Network status", "Benefits/permissions", "Activity/room use", "Evidence", "Renewal/change"] },
      { type: "apxref", text: "Appendix B sets out the fuller governance rationale that sits behind this strategy.", appendixId: "appendix-b" },

      { type: "subhead", text: "Volunteer Scheduling: A Related but Distinct Problem" },
      {
        type: "p",
        text: "Volunteer onboarding alone does not solve the operational problem. The Mill also needs an easier way to understand regular volunteer availability, know which roles or services a volunteer is willing and able to support, plan recurring services, identify gaps, find appropriate replacement cover, and reduce repeated WhatsApp, telephone and email discovery.",
      },
      {
        type: "p",
        text: "The target is not to turn volunteers into assets that can be automatically allocated. The distinction is:",
      },
      { type: "quote", text: "Availability means \u201cyou may ask me\u201d. Acceptance means \u201cI have committed\u201d." },
      { type: "p", text: "The desired journey, at a high level:" },
      { type: "flowchain", steps: ["Service need", "Suitable & potentially available volunteers", "Invitation", "Acceptance", "Confirmed cover", "Exception / replacement"] },
      { type: "p", text: "And for cancellation:" },
      { type: "flowchain", steps: ["Cancellation", "Coverage gap", "Suitable backups", "Ask", "Accept", "Rota updated"] },
      { type: "p", text: "The administrator manages matches and exceptions rather than repeatedly discovering availability manually." },
      { type: "p", text: "The programme principle is:" },
      { type: "quote", text: "Rooms first for implementation. Volunteer scheduling early for exploration." },
      {
        type: "p",
        text: "Volunteer scheduling / community management: candidate to be confirmed through bounded discovery and supplier demonstration. Plinth remains one possible candidate for broader volunteer, Network Organisation and impact capability; LemonBooking or another commodity service may prove sufficient for scheduling and availability.",
      },
      {
        type: "apxref",
        text: "Appendix B sets out the volunteer profile and service profile model, a small discovery sandbox using synthetic data, and the criteria a candidate application should be tested against.",
        appendixId: "appendix-b",
      },
    ],
  },
  {
    id: "roadmap",
    number: 7,
    title: "24-Month Roadmap",
    why: "The roadmap shown in Visual 03 above is achievable at a modest, steady pace; this section explains the discipline that keeps it that way.",
    blocks: [
      {
        type: "p",
        text: "The programme should be achievable with approximately 2\u00bd volunteer days per month, provided vendors, suppliers and operational owners perform routine implementation and operation.",
      },
      {
        type: "p",
        text: "A new system should not automatically trigger migration of everything associated with that subject. Instead: establish a measurable problem, record the current baseline, configure one bounded journey, test it with real users, compare the result with the baseline, improve if necessary, adopt, retire the displaced process, and move to the next journey.",
      },
      { type: "quote", text: "Configure before integrating. Integrate before custom-building. Custom-build only where repeated evidence demonstrates an unresolved problem." },
      { type: "quote", text: "The important adoption gate is: do not scale a new process until normal users can operate it without specialist or volunteer rescue." },
      {
        type: "p",
        text: "Every material change should also begin with a baseline and be checked afterwards against the same measures. For room booking, for example: number of enquiries, response time, time to confirmation, hours of administration, booking conversion, occupancy and income, measured both before and after each change.",
      },
      { type: "quote", text: "The test is not: \u201cDoes the new application work?\u201d It is: \u201cDid the operating problem improve?\u201d" },
      { type: "p", text: "Every introduction creates disruption, so the programme must identify not only what starts, but what stops." },
      { type: "flowchain", steps: ["Prove", "Adopt", "Stabilise", "Retire"] },
      { type: "quote", text: "Retirement should follow successful adoption, not precede it." },
      {
        type: "p",
        text: "Legacy applications should not survive indefinitely merely because removing them feels uncomfortable; equally, familiar systems should not be removed merely because a new platform contains a similar feature. The criterion is operational value.",
      },
      { type: "apxref", text: "Appendix A lists the specific retirement candidates identified against each operational area. Appendix G sets out how progress against the roadmap should be measured.", appendixId: "appendix-a" },
    ],
  },
  {
    id: "resilience-training",
    number: 8,
    title: "Resilience and Training: Building Independence, Not Dependence",
    why: "The programme must not create a new dependency on one volunteer or one system; every capability should work even if that person is unavailable.",
    blocks: [
      { type: "quote", text: "The transition must not depend on the transition volunteer." },
      {
        type: "p",
        text: "Volunteer support can materially reduce the cost and risk of the programme, helping The Mill understand its current journeys, configure and test new processes, establish baseline measures, support initial training and ensure old processes are retired once replacements work. However, the volunteer must not become part of the permanent operating architecture.",
      },
      { type: "quote", text: "If the transition volunteer became unavailable tomorrow, could The Mill continue operating safely and know where to obtain help?" },
      {
        type: "p",
        text: "Every capability should therefore be designed for organisational self-sufficiency from the outset. No important Mill process should depend on one volunteer, one member of staff, one personal Google account, one supplier contact, one undocumented Zap, one spreadsheet understood by one person, or one person's memory.",
      },
      { type: "apxref", text: "Appendix C sets out what each important service should have instead, and the detailed continuity checklist.", appendixId: "appendix-c" },
      {
        type: "p",
        text: "The same test applies to volunteer scheduling: a nominated deputy should be able to see services requiring volunteers, confirmed cover, gaps, potentially suitable replacements, accepted commitments and immediate exceptions, without needing somebody else's personal WhatsApp history, inbox or memory.",
      },
      { type: "quote", text: "The organisation knows the state of the rota; the coordinator does not have to remember it." },
      {
        type: "p",
        text: "Training should not consist of a large system manual; for each role, a small set of practical tasks and a one-page operating card is usually enough. A useful adoption measure is therefore not \u201cHow many people attended training?\u201d but:",
      },
      { type: "quote", text: "\u201cCan two ordinary users independently complete the essential tasks?\u201d" },
      {
        type: "p",
        text: "The discovery also recommends practical task-based training, volunteer support, paper fallback where necessary and measuring completion and confidence.",
      },
      { type: "p", text: "Over time, appropriate administrators and lead users should progressively become:" },
      {
        type: "list",
        variant: "flow",
        items: ["Confident super-users", "Owners of their operational journeys", "First-line problem solvers", "Workflow improvers", "Informed users of supplier support", "Practical users of AI-assisted tools"],
      },
      {
        type: "p",
        text: "AI assistance should support exploration, analysis and drafting; it should never independently change live permissions, policies or organisational authority. Their emerging capability, not a software-developer skillset, should be expressed through questions such as:",
      },
      {
        type: "list",
        variant: "grid",
        items: [
          "What problem are we trying to solve?",
          "Where is the friction occurring?",
          "Can the existing application already solve it?",
          "What is the smallest change we could test?",
          "How will we know whether it improved the experience?",
        ],
      },
      { type: "apxref", text: "Appendix D sets out detailed role-by-role training tasks and the specific ways AI assistance can be used.", appendixId: "appendix-d" },
      {
        type: "p",
        text: "Commodity software materially helps here because The Mill is not expected to maintain the underlying technology itself; licence cost is unlikely to be the principal financial risk.",
      },
      { type: "apxref", text: "Appendix E sets out current supplier support and pricing detail for LemonBooking, Plinth, membermojo and Mailchimp.", appendixId: "appendix-e" },
    ],
  },
  {
    id: "five-year-cost",
    number: 9,
    title: "A Five-Year Cost Proposition",
    why: "The Mill is not being asked to commit to a fixed technology contract; it is being asked to approve a controlled envelope, released only as benefit is proved.",
    blocks: [
      {
        type: "p",
        text: "Rather than approve a five-year technology contract, The Mill could establish a five-year digital operating-model envelope and release expenditure progressively against demonstrated benefit. An indicative planning position is approximately \u00a315,000 over five years. Make clear that this is:",
      },
      {
        type: "list",
        variant: "flow",
        items: ["An indicative planning allowance", "Not a supplier quotation", "Not a commitment to spend", "Released incrementally against demonstrated benefit"],
      },
      { type: "apxref", text: "Appendix F sets out the detailed budget breakdown and assumptions.", appendixId: "appendix-f" },
      {
        type: "p",
        text: "That envelope should be released in phases aligned with the roadmap, authorised only once the previous phase has demonstrated benefit, not committed as a fixed annual schedule. Part of it should be explicitly ring-fenced as a resilience reserve, assuming an external specialist rate of approximately \u00a3600 per day purely as a prudent planning allowance, enough to cover roughly five specialist days across five years for events such as a key volunteer's departure, a broken integration or an emergency handover.",
      },
      { type: "quote", text: "\u201cWe can buy competent help when we need it.\u201d" },
      {
        type: "p",
        text: "The earlier planning figure of approximately \u00a324,000 is not discarded; it is reframed. Expenditure could drift towards \u00a320,000\u2013\u00a324,000 or beyond over five years if the controls in this report are not established, principally through continued dependence on the transition volunteer, weak training, unnecessary integrations and retained duplicate processes.",
      },
      { type: "costcomparison" },
      {
        type: "quote",
        text: "\u00a324k is not the budget ambition. It represents approximately the type of cost exposure that good governance, training, internal capability and disciplined scope control are intended to avoid.",
      },
      {
        type: "p",
        text: "The principal cost-control mechanism is not negotiating slightly cheaper licences. It is building sufficient capability inside The Mill to solve ordinary problems without repeatedly buying external intervention.",
      },
      { type: "p", text: "What trustees should therefore be approving is not:" },
      { type: "quote", text: "\u201c\u00a315,000 for a CRM.\u201d" },
      { type: "p", text: "but:" },
      {
        type: "quote",
        text: "\u201cUp to \u00a315,000 over five years to create and sustain a simpler, resilient digital operating model, subject to phased proof of benefit.\u201d",
      },
      {
        type: "p",
        text: "This discipline applies directly to volunteer scheduling: The Mill should not add another application for this purpose unless the demonstrated benefit exceeds the additional licence cost, training, support, data duplication, integration and continuity burden it would introduce.",
      },
    ],
  },
  {
    id: "north-star-dynamic-profile",
    number: 10,
    title: "Future-State North Star and The Mill's Dynamic Profile",
    why: "This is the long-term destination worth keeping in view, even though the current programme is deliberately practical: a Mill that increasingly knows itself, and can present that knowledge to whoever needs it.",
    blocks: [
      {
        type: "p",
        text: "The immediate programme is deliberately practical: simplify the website, improve room booking, strengthen volunteer and organisation journeys, reduce duplicated administration and produce better operational evidence. There is, however, value in keeping sight of a longer-term destination.",
      },
      {
        type: "p",
        text: "As The Mill's network grows, it should not require The Mill to become the central administrator or data custodian for every participating organisation and individual. A more scalable model would allow:",
      },
      {
        type: "list",
        variant: "grid",
        items: [
          "Community organisations to retain responsibility for their own members and participant relationships",
          "The Mill to retain authority over its rooms, facilities and operating policies",
          "Rooms and other assets to maintain structured profiles describing capacity, accessibility, facilities, availability, permitted uses and pricing conditions",
          "Network Organisations to establish reusable profiles and appropriate entitlements",
          "Only the information necessary for a particular booking, activity or report to be shared",
          "Routine requests increasingly to be matched against available facilities and policies automatically",
          "Human judgement to remain available for exceptions, concessions, safeguarding, community priorities and other consequential decisions",
          "Operational activity to create proportionate evidence for learning, funding and improvement",
        ],
      },
      {
        type: "quote",
        text: "In that future state, a room could increasingly appear to \u201cbook itself\u201d: a request is matched to an appropriate space, conditions are checked, an authorised price is offered, payment is taken and the booking proceeds unless a defined exception requires human attention. This is not a proposal for autonomous AI or the removal of community judgement. It is a vision of bounded automation operating within policies and authority established by The Mill.",
      },
      {
        type: "p",
        text: "The purpose of naming this now is not to build the future system during the current programme. It is to ensure that today's decisions do not prevent it. This favours:",
      },
      {
        type: "list",
        variant: "flow",
        items: [
          "Modular rather than monolithic applications",
          "Clear ownership and authoritative sources",
          "Usable exports and interfaces",
          "Distributed responsibility",
          "Minimum necessary data sharing",
          "Explicit policies and permissions",
          "Technology that can be replaced incrementally",
        ],
      },
      {
        type: "callout",
        heading: "KATLAS design reference",
        text: "KATLAS has explored this type of future-state community architecture internally under the working title CommunityHubCierge. It is not a recommendation, procurement requirement or part of the 24-month plan; it is referenced solely as a future-state design exercise.",
      },
      { type: "apxref", text: "Appendix H sets out the full CommunityHubCierge provenance note.", appendixId: "appendix-h" },

      { type: "subhead", text: "The Mill's Dynamic Profile" },
      {
        type: "p",
        text: "As the programme progressively establishes authoritative information about rooms, activities, volunteering, policies, pricing, participation and evidence, The Mill begins, almost by stealth, to build a dynamic authorised profile of itself, a single, trusted, living picture, not a giant central database. The important concept is:",
      },
      { type: "quote", text: "Single authorised versions of important facts, presented through different purpose-appropriate views." },
      {
        type: "deflist",
        items: [
          { term: "Hirer view", def: "relevant rooms, capacity, facilities, accessibility, availability, permitted uses, prices and Terms & Conditions." },
          { term: "Resident view", def: "What's On, activities, accessibility, organisations, ways to participate or volunteer." },
          { term: "Network Organisation view", def: "facilities, network opportunities, room access, relevant benefits, participation routes and promotional opportunities." },
          { term: "Volunteer view", def: "roles, opportunities, support, practical guidance and activities." },
          { term: "Reception/administration view", def: "today's authorised operational state and the exceptions requiring attention." },
          { term: "Trustee view", def: "utilisation, earned income, participation, community reach, volunteer contribution, risk and improvement evidence." },
          { term: "Funder/reviewer view", def: "authorised evidence relevant to the particular programme, outcome, obligation or KPI." },
        ],
      },
      { type: "p", text: "These are not competing descriptions of The Mill. They are different authorised views of the same organisational reality." },
      { type: "dynamicmap" },

      { type: "subhead", text: "The Future Customer Experience" },
      {
        type: "p",
        text: "The longer-term ambition is that customers and partners increasingly describe what they need rather than having to understand The Mill's internal administration, for example:",
      },
      {
        type: "quote",
        text: "\u201cI need somewhere on Saturday afternoon for 18 children and parents, with wheelchair access, tables and somewhere to wash art materials.\u201d",
      },
      { type: "quote", text: "The customer describes the need. The Mill presents the best authorised match." },
      {
        type: "p",
        text: "This is also the proper context for the existing \u201croom books itself\u201d language. It means bounded self-service within policies, information and authority already established by The Mill, not uncontrolled autonomous decision-making.",
      },

      { type: "subhead", text: "Evidence That Emerges From Activity" },
      {
        type: "p",
        text: "A more structured operational model should also make it substantially easier for The Mill to produce credible, timely evidence for trustees, funders and reviewers, because evidence increasingly emerges from normal activity rather than being reconstructed afterwards for a report.",
      },
      { type: "apxref", text: "Appendix G sets out the specific measures this can draw on.", appendixId: "appendix-g" },
    ],
  },
];

export const RECOMMENDATIONS = [
  "Approve this as an operating-model programme, not a CRM procurement exercise.",
  "Use the three rooms as the first bounded proof, establishing before/after measures before implementation.",
  "Pilot LemonBooking as both the room-commerce capability and potential simpler web environment.",
  "After the room journey is stable, pilot the simplest volunteer scheduling solution that can match volunteer availability and capability to service needs, manage acceptance and replacement cover, and remain operable by ordinary Mill users. Use a broader community-management platform only where additional demonstrated requirements justify it.",
  "Pilot membermojo with one Network Organisation, preserving organisation ownership of its members.",
  "Adopt distributed ownership and authority: each form/policy/process has a named owner; systems hold authoritative records; roles determine access; consequential changes have explicit approval authority.",
  "Consolidate marketing around one authoritative Mill audience, while specialist applications retain transactional communications.",
  "Use development tools such as Emergent for prototyping, training and bounded experiments, not as additional systems of record.",
  "Measure before and after every material change and stop implementations that increase administrative burden.",
  "Make retirement part of implementation: every successful new capability should trigger a deliberate decision about the spreadsheet, form, mailing list or manual process it replaces.",
  "Defer bespoke CommunityHub/AI orchestration until evidence demonstrates a problem that commodity systems cannot solve.",
  "Review the programme at 24 months against operational, financial and community outcomes, not against the number of technologies installed.",
];

export const CLOSING_BLOCKS = [
  {
    type: "p",
    text: "The programme is not ultimately about installing a booking system, replacing WordPress or introducing a CRM. It is about enabling The Mill progressively to know itself better, and to make that knowledge useful.",
  },
  {
    type: "list",
    variant: "flow",
    items: [
      "Rooms know what they can support",
      "Activities publish what people need to know",
      "Policies and Terms & Conditions have authoritative versions",
      "Operational systems show current state",
      "Network Organisations retain appropriate responsibility for their people",
      "Administrators increasingly understand and improve their own journeys",
      "A Number 2 can take over when a Number 1 is unavailable",
      "Customers increasingly describe what they need and receive the most appropriate authorised response, with less effort",
    ],
  },
  {
    type: "p",
    text: "The result is not one centralised database. It is a set of trusted, authorised profiles and operational views that together create a dynamic picture of The Mill, one that can be presented differently to a resident, hirer, volunteer, community organisation, administrator, trustee or funder while remaining grounded in the same authorised organisational reality.",
  },
  {
    type: "p",
    text: "This is what makes the model resilient and increasingly scalable. As The Mill grows, its knowledge, evidence and service capability should be able to grow without administration and external technical cost increasing at the same rate.",
  },
  {
    type: "list",
    variant: "flow",
    items: [
      "Measure before change",
      "Configure before integrating",
      "Prove before scaling",
      "Adopt before retiring",
      "Automate administration before automating authority",
    ],
  },
  {
    type: "quote",
    text: "Change where the benefit is material and sufficiently broad. Connect where an existing specialist process already works. Intervene where governance or risk requires it. Otherwise, leave it alone.",
  },
];

// ---------------------------------------------------------------------------
// APPENDICES — substantiation, operational detail and assumptions only. No
// appendix introduces a new conclusion; each supports a specific core section.
// ---------------------------------------------------------------------------
export const APPENDICES = [
  {
    id: "appendix-a",
    letter: "A",
    title: "The Full Change Matrix",
    supports: "Target Operating Model and 24-Month Roadmap",
    blocks: [
      {
        type: "p",
        text: "This appendix sets out the full operational Change Matrix summarised in the Target Operating Model section, together with the specific processes recommended for retirement as each area is proved.",
      },
      {
        type: "table",
        columns: ["Area", "Current problem", "Proposed change", "Principal benefit", "Disruption / risk", "Success evidence"],
        rows: [
          ["Rooms", "Email/manual booking, fragmented availability, deposit and invoice handling", "Structured room profiles + ecommerce booking", "Faster booking, increased utilisation and income", "New process for staff/hirers", "Booking time, occupancy, conversion, admin time"],
          ["Website", "WordPress complexity and reliance on specialist knowledge", "Simpler managed web environment integrated with services", "Easier publishing and lower dependency", "Migration/content rationalisation", "Routine changes performed by non-specialists"],
          ["Volunteers", "Forms, email, spreadsheets and local records", "Bounded Plinth volunteer journey", "Faster onboarding, better handover", "Adoption/data migration", "Time to active role, chasing, retention"],
          ["Network Organisations", "Relationships depend partly on individual knowledge", "Organisation profile and network status", "Stronger network and easier repeat engagement", "Requires common definitions", "Active organisations, repeat collaboration"],
          ["Membership", "Potential temptation to centralise everyone at The Mill", "Organisation-owned membermojo where required", "Local ownership, reusable membership and attendance", "Data-controller relationship must remain clear", "Repeat participation, admin reduction"],
          ["Communications", "Multiple mailing lists and channels", "One marketing consent/audience source", "Better targeting and governance", "List clean-up", "Duplicate lists, opt-outs, campaign conversion"],
          ["Forms", "Duplicate/obsolete forms and spreadsheet destinations", "Named owner per form + register + authority to publish/change", "Clear responsibility without central bureaucracy", "Initial inventory", "Number of duplicate/obsolete forms"],
          ["Policies", "Policies and operational processes can diverge", "Named policy owner + approval authority + authoritative published version", "Better assurance and consistency", "Initial governance work", "Review compliance and fewer conflicting versions"],
          ["Data visibility", "Broad/inconsistent access", "Custodial systems + role-based permissions", "Less privacy risk and simpler working views", "Configuration", "Fewer unnecessary copies/access rights"],
          ["Evidence", "Reporting reconstructed after activity", "Compact evidence spine generated by operations", "Better Prove & Improve and funder readiness", "Risk of over-measurement", "Reporting time and evidence completeness"],
          ["Google/Notion", "Tools sometimes act as databases/workflows", "Return them to bounded document/knowledge roles", "Less duplication", "Behavioural change", "Fewer live operational spreadsheets"],
          ["Grants/finance", "Disconnected obligations, codes and reporting", "Lightweight grant register + stable finance references", "Better handover and reporting", "Avoid duplicating finance", "Reporting deadlines/errors"],
          ["Activities/art", "Risk of adding a new system for each initiative", "Reuse rooms/events/network architecture", "Lower cost and complexity", "Some edge cases remain", "Number of additional systems avoided"],
        ],
      },
      {
        type: "caption",
        text: "\u201cNamed owner\u201d above means a role, not necessarily one named individual; see Appendix C, The Continuity Test, for why ownership is designed around roles (with a deputy and organisational access) rather than a single person, and who holds access as a result.",
      },
      { type: "subhead", text: "Retirement Candidates Identified During Discovery" },
      {
        type: "p",
        text: "Every introduction creates disruption, so the programme must identify not only what starts, but what stops. Potential retirement candidates include:",
      },
      {
        type: "list",
        variant: "flow",
        items: [
          "Duplicate Google Forms",
          "Booking spreadsheets",
          "Manually maintained room calendars",
          "Overlapping mailing lists",
          "Operational records in personal drives",
          "Duplicate event listings",
          "Redundant Zapier processes",
          "Notion databases replaced by authoritative operational applications",
        ],
      },
    ],
  },
  {
    id: "appendix-b",
    letter: "B",
    title: "Governance, Roles and Journey Detail",
    supports: "Target Operating Model and Website & Network Strategy",
    blocks: [
      {
        type: "p",
        text: "This appendix substantiates the governance and network strategy described in the Target Operating Model and Website and Network Strategy sections.",
      },
      { type: "subhead", text: "Why Ownership Is Distributed, Not Centralised" },
      {
        type: "p",
        text: "The existing working material explicitly rejects creating one central Forms Owner or Data Owner and instead proposes individual artefact ownership, role-controlled system access and defined approval authority.",
      },
      { type: "subhead", text: "Supporting Tools and Emergent-Type Development" },
      { type: "quote", text: "Development platforms such as Emergent can be useful, but they should not become another core operational system." },
      {
        type: "p",
        text: "Their appropriate roles include rapid prototypes, testing website concepts, staff training simulations, demonstrating future journeys, lightweight navigation/front-door tools, reporting views where supported APIs permit, and testing future room-suitability logic. They should not duplicate authoritative booking, membership, volunteer or financial records. This provides The Mill with a low-cost design laboratory without creating another production platform that requires maintenance.",
      },
      { type: "subhead", text: "Volunteer Scheduling: Profile-and-Match Discovery" },
      {
        type: "p",
        text: "This substantiates the volunteer scheduling subsection in Website and Network Strategy. A volunteer profile may include only proportionate volunteering information such as:",
      },
      {
        type: "list",
        variant: "flow",
        items: ["Roles/interests", "Relevant induction or checks", "Usual availability", "Preferred frequency", "Locations/activities they are comfortable supporting", "Willingness to act as backup", "Temporary unavailability"],
      },
      { type: "p", text: "A service profile may include:" },
      {
        type: "list",
        variant: "flow",
        items: ["Service/activity", "Date/time", "Number of volunteers required", "Role/capability required", "Responsible lead", "Backup requirement"],
      },
      { type: "subhead", text: "A Candidate-Neutral Discovery Sandbox" },
      {
        type: "p",
        text: "The existing Emergent/web sandbox may be used to model the desired journey using synthetic volunteers and synthetic services. It must not become the production scheduling system. The purpose is to define what The Mill needs from a real application, not to build that application.",
      },
      {
        type: "deflist",
        items: [
          { term: "Sue", def: "Reception / Welcome. Usually available Tuesday mornings. Preferred frequency: twice per month. Backup: yes." },
          { term: "David", def: "Reception / Welcome. Usually available Tuesday and Thursday. Backup: yes." },
          { term: "Tuesday Welcome Desk", def: "10:00\u201313:00. One reception-ready volunteer required; one backup desirable." },
        ],
      },
      { type: "p", text: "Using synthetic information only, the sandbox should demonstrate:" },
      { type: "quote", text: "Service need \u2192 matching profiles \u2192 invitation \u2192 acceptance \u2192 coverage \u2192 cancellation \u2192 replacement." },
      { type: "subhead", text: "Application Selection Criteria" },
      { type: "p", text: "The subsequent candidate demonstration should test whether a commodity application can:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Allow volunteers to maintain their own relevant availability",
          "Support recurring availability",
          "Record appropriate roles/capabilities/checks",
          "Describe service/shift requirements",
          "Identify suitable potentially available volunteers",
          "Allow invitation and accept/decline rather than automatic allocation",
          "Show filled and unfilled cover",
          "Reopen a requirement after cancellation",
          "Help identify suitable replacement cover",
          "Provide a clear current rota to an authorised Number 2",
          "Record attendance/hours where useful",
          "Avoid unnecessary duplication of volunteer data",
          "Remain simple enough for ordinary administrators and volunteers to use",
        ],
      },
      { type: "p", text: "The decision test is:" },
      { type: "quote", text: "Can The Mill plan and recover volunteer-supported services without beginning a WhatsApp, telephone or email discovery exercise?" },
    ],
  },
  {
    id: "appendix-c",
    letter: "C",
    title: "Continuity Checklists",
    supports: "Alison and the Number 2 Test, and Resilience and Training",
    blocks: [
      {
        type: "p",
        text: "This appendix sets out the detailed continuity checklist referred to in the Alison and Number 2 worked example and the Resilience and Training section.",
      },
      { type: "subhead", text: "The Continuity Test" },
      { type: "p", text: "No important Mill process should depend upon:" },
      {
        type: "list",
        variant: "flow",
        items: ["One volunteer", "One member of staff", "One personal Google account", "One supplier contact", "One undocumented Zap", "One spreadsheet understood by one person", "One person's memory"],
      },
      { type: "p", text: "Each important service should instead have:" },
      {
        type: "deflist",
        items: [
          { term: "An authoritative system", def: "where the current operational record lives." },
          { term: "An operational owner and deputy", def: "so absence does not stop the process." },
          { term: "Role-based access", def: "held through organisational rather than personal credentials wherever possible." },
          { term: "A short operating guide", def: "covering the handful of tasks the role actually performs." },
          { term: "A supplier/support route", def: "so The Mill knows who to contact when something fails." },
          { term: "An export/recovery route", def: "so information is not trapped inside one supplier." },
          { term: "A manual fallback", def: "for genuinely critical activities." },
        ],
      },
      { type: "subhead", text: "The Number 2 Test in Detail" },
      { type: "p", text: "If a Number 1 becomes unavailable, a nominated deputy should be able to quickly understand:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Current class capacity",
          "Confirmed participants",
          "Waiting list",
          "Cancellations",
          "Relevant payment state where required",
          "Outstanding exceptions",
          "Current published information",
          "Current Terms & Conditions",
          "Immediate actions requiring attention",
        ],
      },
      {
        type: "p",
        text: "The deputy should not need the Number 1's email credentials, a private spreadsheet, historic correspondence, undocumented local knowledge, or direct contact with the Number 1.",
      },
      { type: "subhead", text: "The Administration Journey" },
      { type: "p", text: "The intention is not merely to give administrators different software. It is to give them a clearer operating journey:" },
      { type: "flowchain", steps: ["See", "Act", "Understand", "Learn", "Improve"] },
      { type: "p", text: "For example, room administration should progressively allow a member of staff to see:" },
      {
        type: "list",
        variant: "flow",
        items: [
          "What is booked today",
          "What remains available",
          "Which enquiries are awaiting action",
          "What has been paid",
          "Which bookings need attention",
          "Where enquiries are being lost",
          "Which rooms/times are underused",
          "What customers are asking for",
          "What feedback is being received",
          "What practical change might improve the experience",
        ],
      },
      {
        type: "p",
        text: "The administrator therefore moves from being the person who manually joins systems together to being someone able to understand and improve how The Mill operates. The same principle should later apply to volunteers, Network Organisations, activities and participation.",
      },
    ],
  },
  {
    id: "appendix-d",
    letter: "D",
    title: "Training and AI-Assisted Working",
    supports: "Resilience and Training",
    blocks: [
      {
        type: "p",
        text: "This appendix sets out the detailed, role-by-role training approach and the boundaries for AI-assisted working referred to in the Resilience and Training section.",
      },
      { type: "subhead", text: "Training-Lite Means Designing for Independence" },
      { type: "p", text: "Training should not consist of a large system manual. For each role, create a small set of practical tasks. For example:" },
      {
        type: "deflist",
        items: [
          { term: "Reception", def: "see today's rooms; confirm booking status; identify the responsible organiser; know what to do when something is wrong." },
          { term: "Bookings", def: "alter availability; process an exception; amend a booking; understand payment status." },
          { term: "Communications", def: "publish an event; update a page; prepare the approved audience for a campaign." },
          { term: "Volunteer lead", def: "onboard a volunteer; identify outstanding steps; change status; close a volunteer record appropriately." },
        ],
      },
      { type: "p", text: "Each role should have a one-page operating card and, where useful, a short screen recording or interactive training example." },
      {
        type: "p",
        text: "Administrators should not simply be trained to \u201cuse the new systems\u201d. The programme should progressively enable appropriate administrators and lead users to become:",
      },
      {
        type: "list",
        variant: "flow",
        items: ["Confident super-users", "Owners of their operational journeys", "First-line problem solvers", "Workflow improvers", "Informed users of supplier support", "Practical users of AI-assisted tools"],
      },
      { type: "p", text: "Agentic AI and similar assistance can increasingly help suitably trained users:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Explore product capabilities",
          "Interpret supplier guidance",
          "Analyse repetitive questions",
          "Draft FAQs",
          "Examine a workflow",
          "Compare configuration alternatives",
          "Prepare forms/content",
          "Develop training material",
          "Document an operating journey",
          "Prepare a clear support request",
          "Explore low-cost improvements before commissioning external work",
        ],
      },
      {
        type: "quote",
        text: "AI assists exploration, analysis and design. It does not independently change live permissions, policies, consequential rules or organisational authority.",
      },
    ],
  },
  {
    id: "appendix-e",
    letter: "E",
    title: "Supplier Capability and Pricing",
    supports: "Resilience and Training, and Five-Year Cost Proposition",
    blocks: [
      {
        type: "p",
        text: "This appendix records current supplier capability and pricing notes referred to in the Resilience and Training and Five-Year Cost sections. These are supplier-published positions at the time of writing, not guarantees, and should be reconfirmed before relying on them.",
      },
      {
        type: "list",
        inline: true,
        items: [
          "LemonBooking currently includes vendor support Monday\u2013Friday, a help centre and email support; it also offers a 30-day trial and says it will configure the initial system without a setup fee. Its plans support multiple staff accounts and role-based access.",
          "membermojo's current standard pricing is also modest: up to 500 members costs \u00a395 per organisation per year, including the membership database, renewal reminders, mailing lists, attendance and membership cards.",
          "Plinth currently describes a free plan for small charities/CICs covering members, volunteers, surveys, bookings, payments, room hire and impact measurement, although The Mill should confirm its eligibility and any future paid requirements directly before relying on that assumption.",
          "Mailchimp currently provides a free marketing tier up to 250 contacts and 500 sends per month; paid pricing then depends on audience size and plan.",
        ],
      },
      {
        type: "quote",
        text: "The licence cost is therefore unlikely to be the principal financial risk. The larger risk is people dependency, configuration, transition and future support.",
      },
      {
        type: "p",
        text: "Payment-processing charges should normally be treated separately as a variable cost of receiving booking/event income rather than as part of the digital-transformation budget. LemonBooking, for example, currently advertises a 1.29% card-payment rate through its SumUp arrangement.",
      },
    ],
  },
  {
    id: "appendix-f",
    letter: "F",
    title: "Five-Year Budget Detail",
    supports: "A Five-Year Cost Proposition",
    blocks: [
      {
        type: "p",
        text: "This appendix sets out the detailed budget breakdown and assumptions behind the indicative \u00a315,000 five-year envelope referred to in the Five-Year Cost Proposition section.",
      },
      {
        type: "table",
        totalRow: true,
        columns: ["Five-year provision", "Indicative allowance"],
        rows: [
          ["Core applications, website and modest add-ons", "\u00a33,000"],
          ["Initial configuration / migration assistance", "\u00a32,500"],
          ["Training, super-user development and handover", "\u00a32,500"],
          ["External specialist support reserve", "\u00a33,000"],
          ["Small integrations / reporting improvements where justified", "\u00a31,500"],
          ["Contingency / supplier price change", "\u00a32,500"],
          ["Indicative five-year envelope", "\u00a315,000"],
        ],
      },
      { type: "p", text: "Make clear that this is:" },
      {
        type: "list",
        variant: "flow",
        items: ["An indicative planning allowance", "Not a supplier quotation", "Not a commitment to spend", "Released incrementally against demonstrated benefit"],
      },
      { type: "subhead", text: "Releasing the Envelope in Phases" },
      {
        type: "p",
        text: "The \u00a315,000 envelope should be released in phases aligned with the roadmap horizons, with each phase authorised only once the previous phase has demonstrated benefit, rather than committed as a fixed annual schedule. If Plinth remains free, member organisations fund their own membermojo accounts, LemonBooking removes separate web-hosting expenditure and staff become self-sufficient quickly, actual expenditure could be materially lower. Conversely, the reserve means that the programme does not collapse if the volunteer disappears or The Mill needs several days of paid specialist help.",
      },
      { type: "subhead", text: "The Resilience Reserve" },
      { type: "p", text: "I would explicitly ring-fence part of the \u00a315,000 as:" },
      { type: "quote", text: "\u201cWe can buy competent help when we need it.\u201d" },
      {
        type: "p",
        text: "For budgeting purposes, The Mill could assume an external specialist planning rate of approximately \u00a3600 per day, purely as a prudent internal allowance, not a claimed market or supplier rate. A \u00a33,000 continuity reserve therefore represents roughly 5 specialist days across five years.",
      },
      { type: "p", text: "That is enough to deal with events such as:" },
      {
        type: "list",
        variant: "flow",
        items: ["Key volunteer departure", "Administrator replacement", "Significant supplier configuration issue", "Data export/migration", "Website restructuring", "Broken integration", "Annual health check", "Emergency handover"],
      },
      { type: "p", text: "The existence of that reserve itself reduces organisational anxiety." },
      { type: "subhead", text: "The Avoidable Risk in Detail" },
      { type: "p", text: "The earlier planning figure of approximately \u00a324,000 is not discarded. It is reframed. Expenditure could drift towards approximately \u00a320,000\u2013\u00a324,000 or beyond over five years if the controls proposed in this report are not established. Potential causes include:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Administrators remaining dependent on the transition volunteer",
          "Every configuration change requiring external help",
          "Weak staff engagement",
          "Inadequate training",
          "Unnecessary integrations",
          "Retaining duplicate legacy processes",
          "Overconfiguration",
          "Commissioning bespoke development prematurely",
          "Poor documentation",
          "Lack of deputies",
          "Treating AI as another specialist technology rather than an accessible support capability",
        ],
      },
      { type: "subhead", text: "The Self-Sufficiency Test" },
      { type: "p", text: "By the later stages of the programme, an appropriate administrator or lead user should increasingly be able to:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Identify an operational problem",
          "Examine relevant evidence",
          "Explore alternatives using existing systems, supplier guidance and AI assistance",
          "Design a small bounded improvement",
          "Test it",
          "Involve appropriate authority where required",
          "Measure the result",
          "Document what changed",
          "Enable a deputy to understand the journey",
          "Request external specialist support only when genuinely necessary",
        ],
      },
      { type: "p", text: "The success measure is not simply:" },
      { type: "quote", text: "\u201cDid The Mill stay below \u00a315,000?\u201d" },
      { type: "p", text: "It is:" },
      {
        type: "quote",
        text: "\u201cHas The Mill developed sufficient internal capability that routine operational improvement no longer depends upon an external technologist or transition volunteer?\u201d",
      },
      { type: "subhead", text: "What Each Phase Should Demonstrate" },
      { type: "flowchain", steps: ["Before", "Change", "After"] },
      { type: "p", text: "and answer:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Did the experience improve?",
          "Did administrative effort fall?",
          "Can normal staff/volunteers operate it?",
          "Is ownership and authority clear?",
          "Can The Mill obtain help without relying on one individual?",
          "What existing process can now be retired?",
          "Is the next investment still justified?",
        ],
      },
      {
        type: "quote",
        text: "If those conditions are not met, further expenditure should stop rather than continuing because a roadmap says so.",
      },
    ],
  },
  {
    id: "appendix-g",
    letter: "G",
    title: "Measurement and Evidence Definitions",
    supports: "24-Month Roadmap and Future-State North Star",
    blocks: [
      { type: "p", text: "This appendix sets out the measurement approach and detailed evidence list referred to throughout the core report." },
      { type: "subhead", text: "Measure Before and After Build" },
      {
        type: "p",
        text: "Every material change should begin with a baseline. For room booking, for example, before: number of enquiries; response time; time to confirmation; manual hand-offs; hours of administration; booking conversion; occupancy; cancellations; income. After: measure the same things.",
      },
      {
        type: "p",
        text: "The same principle applies to: volunteer onboarding; web publishing; mailing campaigns; reporting; grant administration; participation; stakeholder onboarding.",
      },
      { type: "quote", text: "The test is not: \u201cDoes the new application work?\u201d It is: \u201cDid the operating problem improve?\u201d" },
      {
        type: "p",
        text: "The report should therefore maintain a small evidence spine rather than accumulating dashboards. The existing material recommends measures such as occupancy, income, representative reach, volunteer contribution, satisfaction, unmet demand, administration per booking, duplicated records and reporting effort.",
      },
      { type: "subhead", text: "Evidence That Emerges From Activity" },
      { type: "p", text: "Ordinary activity can increasingly reveal:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Utilisation of community space",
          "Participant volumes",
          "Repeat/new participation",
          "Accessibility and barriers",
          "Representative reach where appropriately measured",
          "Active Network Organisations",
          "Volunteer contribution",
          "Room-hire income",
          "Unmet demand",
          "Customer/participant experience",
          "Outcomes and learning",
          "Changes made in response to evidence",
        ],
      },
      { type: "quote", text: "Evidence should increasingly emerge from normal activity rather than being reconstructed afterwards for a report." },
      { type: "p", text: "This provides a stronger demonstration of success because The Mill can increasingly show:" },
      { type: "flowchain", steps: ["What happened", "What was learned", "What changed"] },
      { type: "p", text: "without constructing a separate reporting bureaucracy." },
    ],
  },
  {
    id: "appendix-h",
    letter: "H",
    title: "CommunityHubCierge Provenance",
    supports: "Future-State North Star",
    blocks: [
      { type: "p", text: "This appendix provides the full provenance note for the CommunityHubCierge reference in the Future-State North Star section." },
      {
        type: "callout",
        heading: "KATLAS design reference",
        text: "KATLAS has explored this type of future-state community architecture internally under the working title CommunityHubCierge. It has not previously been proposed to The Mill and is not a recommendation, procurement requirement or part of the 24-month implementation plan. It is referenced here solely as a future-state design exercise: an illustration of how a network of community organisations, people and physical assets might eventually coordinate while retaining local ownership, privacy and accountable authority. The value of that exercise is therefore the north star, not the software.",
      },
    ],
  },
];

// VISUAL 01 — As-is → Change → Target State
export const VISUAL_01 = {
  id: "visual-01",
  number: "Visual 01",
  title: "As-is → Change → Target State",
  intro:
    "The central architecture picture: how accumulated tools and working practices move through a deliberate simplification-and-proof strategy into a connected, distributed operating model.",
  rows: [
    ["WordPress needs specialist support", "Simpler web publishing", "One simple public front door"],
    ["Forms → email → spreadsheets", "One authoritative destination per process", "Information captured once"],
    ["Room enquiry/approval/payment disconnected", "LemonBooking room proof", "Rooms largely self-service / semi-booking"],
    ["Volunteer records fragmented", "Volunteer scheduling / service-match proof", "Clear volunteer lifecycle and service coverage"],
    ["Groups/participants mixed into Mill records", "Network Organisation model", "Organisations retain their own people"],
    ["Membership unclear", "membermojo where needed", "Organisation-owned membership/QR"],
    ["Multiple mailing mechanisms", "One marketing consent/audience source", "Mailchimp markets; applications transact"],
    ["Drive used as database/workflow", "Move live operations into specialist systems", "Drive becomes controlled documents/evidence"],
    ["Decision rights often implicit", "Distributed ownership + explicit authority", "People act confidently within roles"],
    ["Reporting reconstructed afterwards", "Measure activity at source", "Evidence emerges from operation"],
    ["Technology learned as \u201csystems\u201d", "Task-based, training-lite adoption", "Staff learn only what their role needs"],
    ["Old processes remain indefinitely", "Prove → Adopt → Retire", "Smaller, clearer technology estate"],
  ],
  millism: "MILLISM: human judgement · accessibility · local ownership · minimum necessary data · distributed authority · low administrative burden",
  notNow: "No enterprise CRM · no wholesale migration · no central Mill member database · no full integration programme · no autonomous AI decision-making · no bespoke CommunityHub platform",
  note: "That line tells trustees what the programme is deliberately refusing to do. The evidence supports this modular direction: existing analysis explicitly warns against over-engineering and supplier dependence and recommends modular capabilities, usable exports and role-specific views.",
};

// VISUAL 02 — Stakeholder / Change Matrix
export const VISUAL_02 = {
  id: "visual-02",
  number: "Visual 02",
  title: "Stakeholder / Change Matrix",
  intro: "The human view of the programme: who changes, and why it is better for them.",
  columns: ["Stakeholder", "Problem today", "Change they experience", "Benefit", "Measure", "Custody / authority"],
  rows: [
    ["Resident / visitor", "Difficult discovery / repeated forms", "Simple website → activity/room/joining route", "Easier participation", "Enquiry → participation conversion", "Website → LemonBooking/Plinth/membermojo"],
    ["Room hirer", "Email, uncertainty, slow confirmation", "Rich room profile → suitability → deposit → booking", "Faster, clearer booking", "Confirmation time / conversion", "LemonBooking"],
    ["Network Organisation", "Relationship partly informal", "Organisation profile + repeat-booking entitlement + shop window", "Easier partnership", "Active/repeat organisations", "Plinth/membermojo (as defined)"],
    ["Organisation member", "Potential repeated onboarding", "Organisation-owned membership/QR", "Portable repeat participation", "Repeat attendance", "Organisation/membermojo"],
    ["Participant", "Risk of unnecessary CRM capture", "Participate without compulsory Mill membership", "Lower friction/privacy", "Completion/dropout", "Plinth/relevant application"],
    ["Volunteer", "Forms/email/chasing", "Profile, availability and service-match journey", "Faster, clearer onboarding", "Time-to-active / retention", "Candidate application \u2014 to be proved"],
    ["Reception", "Memory, folders, incomplete information", "Role-specific \u201ctoday\u201d view", "Better handovers", "Errors / questions / time", "LemonBooking/Plinth (as relevant)"],
    ["Bookings/admin", "Coordinates every step", "Handles exceptions rather than transactions", "Major admin reduction", "Minutes per booking", "LemonBooking"],
    ["Communications", "Multiple lists and duplicated publishing", "Website publishes; one marketing audience", "Clearer campaigns", "Conversion / duplicates", "Mailchimp"],
    ["Finance", "Manual reconciliation", "Booking/payment references flow to finance", "Cleaner reconciliation", "Reconciliation effort", "Finance/Banking Systems"],
    ["Programme leads", "Outcomes hard to see", "Lightweight participation/feedback evidence", "Better improvement decisions", "Reporting effort / actions", "Plinth/relevant applications"],
    ["Trustees", "Assurance requires manual reconstruction", "Aggregate operating view", "Confidence without intrusive access", "Reporting cycle time", "Relevant authoritative systems"],
    ["Funders", "Evidence gathered retrospectively", "Compact evidence spine", "More credible reporting", "Evidence completeness", "Plinth/relevant applications"],
    ["Web/content contributors", "WordPress complexity", "Simpler controlled editing", "Reduced specialist dependence", "Routine changes self-served", "Website CMS"],
  ],
  note: "This visually reinforces the philosophy: not one administrator controlling everything, but distributed ownership supported by controlled systems. Individual items have named owners, information has an authoritative home, users operate through defined roles and consequential actions have defined authority.",
};

// VISUAL 03 — 24-Month Roadmap
export const VISUAL_03 = {
  id: "visual-03",
  number: "Visual 03",
  title: "24-Month Roadmap",
  intro:
    "Four six-month horizons, achievable with approximately 2\u00bd volunteer days per month, provided vendors, suppliers and operational owners perform routine implementation and operation. The roadmap shows incremental change, not a large transformation programme.",
  horizons: [
    {
      range: "0–6 months",
      title: "Prove Rooms",
      kicker: "Understand → Configure → Test → Launch",
      actions: [
        "Three room profiles",
        "Policies/forms ownership",
        "Baseline measures",
        "LemonBooking trial",
        "Simpler web prototype",
        "Deposit/payment journey",
        "Role-based staff training",
        "Parallel discovery: sandbox the volunteer profile / availability / service-match requirement",
      ],
      proof: "Discover → match → book → deposit → pay → use → learn",
      gate: "Can ordinary staff operate it without volunteer/developer rescue?",
      retire: ["Booking spreadsheet", "Duplicate enquiry forms", "Manual calendar/processes"],
    },
    {
      range: "7–12 months",
      title: "Extend to People & Network",
      kicker: "Reuse the pattern",
      actions: [
        "Select and pilot the simplest suitable volunteer scheduling approach, tested against the sandbox requirements",
        "Network Organisation profile",
        "One membermojo partner pilot",
        "Organisation/member boundary",
        "QR/attendance experiment",
        "Feedback / impact capture",
      ],
      proof: "Organisation → activity → participant/member → attendance → aggregate evidence",
      gate: "Has administration fallen and participation become easier?",
      retire: ["Volunteer spreadsheets", "Duplicate participant/member lists", "Redundant forms"],
    },
    {
      range: "13–18 months",
      title: "Connect & Simplify",
      kicker: "Make it feel like one Mill",
      actions: [
        "Website consolidation",
        "Network shop windows",
        "What's On",
        "Mailchimp rationalisation",
        "Policies/forms publication",
        "Finance references",
        "Grant register",
        "Demographic/footfall method",
        "Drive/Notion rationalisation",
      ],
      operatingRule: "Website publishes. Applications transact. Mailchimp markets. Finance accounts.",
      gate: "Are users encountering one coherent service rather than several applications?",
    },
    {
      range: "19–24 months",
      title: "Optimise & Retire",
      kicker: "Measure, then decide",
      measures: [
        "Space: occupancy · unmet demand · conversion",
        "Income: yield · repeat hires · progress toward +50% earned room-hire income",
        "Community: Network Organisations · participation · representative reach",
        "People: volunteer recruitment/retention · repeat participation",
        "Operations: admin time · hand-offs · duplicates · reporting effort",
      ],
      sequence: "Retain → Improve → Retire → Integrate only where justified",
      finalQuestion: "What material problem remains that commodity systems cannot solve?",
      northStarNote:
        "Only at that point explore: Emergent-style prototypes → suitability intelligence → bounded pricing → cross-system proofs → eventual semi-autonomous/community-network orchestration.",
    },
  ],
};

// Orientation aids — navigational/presentational only. All text below is either
// quoted directly from the source (REASSURANCE_QUOTE, THE_ASK, SYSTEMS_REFERENCE
// definitions) or is short reading-guidance about the document itself.

export const REASSURANCE_QUOTE =
  "Training lite, task focused and role specific. Nobody should need to \u201clearn the CRM\u201d. They should learn the small number of tasks required for their role.";

export const THE_ASK = RECOMMENDATIONS[0];

export const READING_PATHS = [
  {
    audience: "Trustees & funders",
    guidance: "Read the Executive Introduction and the three visuals below; that is the full proposition in about five minutes.",
    targetId: "executive-summary",
  },
  {
    audience: "Administrators & staff",
    guidance: "Use the Target Operating Model (Section 5) and the Systems reference in the top nav for who owns what; the full Change Matrix is in Appendix A.",
    targetId: "target-operating-model",
  },
  {
    audience: "Volunteers & workshop leaders",
    guidance: "Section 1 (Millism) explains what does, and doesn't, change for you.",
    targetId: "millism",
  },
];

export const SYSTEMS_REFERENCE = [
  { term: "LemonBooking", def: "rooms, events, booking, deposits/payments and potentially the public website." },
  { term: "Plinth", def: "volunteers, Network Organisations, community activity and impact." },
  { term: "membermojo", def: "organisation-owned membership where Network Organisations require it." },
  { term: "Mailchimp", def: "the authoritative Mill marketing audience and campaign service." },
  { term: "Finance/banking", def: "the authoritative financial record." },
  { term: "Google Shared Drive", def: "controlled documents and supporting evidence." },
  { term: "Notion", def: "internal knowledge and lightweight coordination where useful." },
  { term: "Zapier", def: "limited and documented integration glue." },
];

export const VISUAL_PAGE_FLOW = [
  { label: "Visual 01", role: "System story", question: "Where are we going?" },
  { label: "Visual 02", role: "Human story", question: "Who changes, and why is it better?" },
  { label: "Visual 03", role: "Delivery story", question: "How do we get there without disrupting The Mill?" },
];

export const TOC_ITEMS = [
  { id: "executive-summary", label: "Executive Introduction" },
  { id: "visual-01", label: "Visual 01: As-is → Change → Target" },
  { id: "visual-02", label: "Visual 02: Stakeholder / Change Matrix" },
  { id: "visual-03", label: "Visual 03: 24-Month Roadmap" },
  ...SECTIONS.map((s) => ({ id: s.id, label: `${s.number}. ${s.title}` })),
  { id: "recommendations", label: "Recommendations" },
  ...APPENDICES.map((a) => ({ id: a.id, label: `Appendix ${a.letter}: ${a.title}` })),
];

// ---------------------------------------------------------------------------
// EXECUTIVE SUMMARY — a faithful, shorter presentation of the locked full
// report above. No new recommendation, number, sequence, product role or
// programme boundary is introduced here; content is condensed/re-sequenced
// from the core report and appendices for a 3–5 minute read. Detailed tables,
// the full stakeholder matrix, supplier pricing and budget breakdowns are
// deliberately NOT reproduced here; they remain in the full report.
// ---------------------------------------------------------------------------
export const EXEC_PAGES = [
  {
    id: "exec-finding",
    pageNumber: 1,
    label: "The Finding and the Ask",
    blocks: [
      { type: "p", lead: true, text: "The Mill does not primarily have a CRM problem." },
      {
        type: "p",
        text: "It has a collection of operational journeys: rooms, volunteers, activities, communications, partners, evidence, grants and finance, that have grown across separate tools, spreadsheets, forms, folders and individual working practices.",
      },
      { type: "p", text: "The problem is therefore:" },
      {
        type: "list",
        variant: "flow",
        items: [
          "Fragmented journeys",
          "Duplicated administration",
          "Information held in too many places",
          "Dependency on individual knowledge",
          "Unclear ownership at some hand-offs",
          "Difficulty producing operational evidence",
        ],
      },
      { type: "callout", heading: "The ask", text: "Approve this as an operating-model programme, not a CRM procurement exercise." },
      {
        type: "p",
        text: "The response is deliberately incremental and uses low-cost specialist tools where they already solve the problem well.",
      },
      {
        type: "quote",
        text: "Serve more people, organisations and activities, use The Mill's assets more effectively, increase earned income and produce better evidence without administration increasing at the same rate.",
      },
      { type: "visual", key: "asis" },
    ],
  },
  {
    id: "exec-millism",
    pageNumber: 2,
    label: "What Changes, and What Does Not",
    blocks: [
      { type: "subhead", text: "Millism" },
      { type: "p", text: "Digitisation must preserve what makes The Mill The Mill:" },
      {
        type: "list",
        variant: "flow",
        items: ["Human judgement", "Accessibility", "Local ownership", "Community initiative", "Minimum necessary information", "Distributed responsibility", "Low administrative burden"],
      },
      { type: "p", text: "The programme's decision rule:" },
      {
        type: "quote",
        text: "Change where the benefit is material and sufficiently broad. Connect where an existing specialist process already works. Intervene where governance or risk requires it. Otherwise, leave it alone.",
      },
      { type: "p", text: "This means the programme is not proposing:" },
      {
        type: "list",
        variant: "grid",
        items: [
          "A large enterprise CRM",
          "Wholesale data migration",
          "Centralising every participant/member record",
          "Replacing finance/accounting",
          "Integrating everything immediately",
          "Autonomous AI decision-making",
          "Building CommunityHubCierge during the 24-month programme",
        ],
      },
    ],
  },
  {
    id: "exec-rooms",
    pageNumber: 3,
    label: "Rooms First, Then People",
    blocks: [
      { type: "p", text: "The three rooms are the first proof because they combine:" },
      {
        type: "list",
        variant: "flow",
        items: ["Customer experience", "Availability", "Suitability", "Policy", "Pricing", "Payment", "Access", "Communications", "Income", "Evidence"],
      },
      { type: "flowchain", steps: ["Request", "Match", "Conditions", "Book / human exception"] },
      {
        type: "p",
        text: "\u201cThe room books itself\u201d means routine requests can increasingly proceed through self-service within rules and authority already established by The Mill; unusual or consequential cases still go to people.",
      },
      { type: "p", text: "The same pattern later extends to volunteers, and to Network Organisations and organisation-owned membership." },
      { type: "quote", text: "Rooms first for implementation. Volunteer scheduling early for exploration." },
      { type: "quote", text: "Website publishes. Applications transact. Mailchimp markets. Finance accounts." },
      {
        type: "list",
        variant: "flow",
        items: [
          "LemonBooking: rooms/events/payments and potential simpler web environment",
          "Volunteer scheduling: candidate to be selected after a bounded sandbox proves the required availability, matching, acceptance and replacement-cover journey",
          "membermojo: organisation-owned membership where required",
        ],
      },
      {
        type: "p",
        text: "Plinth remains one possible candidate for broader volunteer, Network Organisation and impact capability; LemonBooking or another commodity service may prove sufficient for scheduling.",
      },
      { type: "p", text: "This is a working technology hypothesis subject to bounded pilots, not a committed procurement package." },
    ],
  },
  {
    id: "exec-people",
    pageNumber: 4,
    label: "People, Resilience and Self-Sufficiency",
    blocks: [
      { type: "visual", key: "alison" },
      { type: "quote", text: "Individual ownership without organisational dependency." },
      { type: "p", text: "This becomes a programme-wide test:" },
      { type: "quote", text: "Can a suitably authorised Number 2 take over without the Number 1 being available?" },
      {
        type: "p",
        text: "The same principle extends to volunteer scheduling. The system may identify appropriate and potentially available volunteers; it must not imply that people are automatically allocated to work.",
      },
      { type: "quote", text: "Availability means \u201cyou may ask me\u201d. Acceptance means \u201cI have committed\u201d." },
      { type: "flowchain", steps: ["Service need", "Suitable & potentially available volunteers", "Invitation", "Acceptance", "Confirmed cover", "Exception / replacement"] },
      { type: "quote", text: "The organisation knows the state of the rota; the coordinator does not have to remember it." },
      { type: "p", text: "Administrators and lead users should progressively become:" },
      {
        type: "list",
        variant: "flow",
        items: ["Confident super-users", "Owners of operational journeys", "First-line problem solvers", "Workflow improvers", "Informed users of supplier support", "Practical users of AI assistance"],
      },
      {
        type: "p",
        text: "AI helps them explore, analyse, draft and test low-cost improvements. It does not independently change permissions, policies or organisational authority.",
      },
      { type: "quote", text: "Can two ordinary users independently complete the essential tasks?" },
    ],
  },
  {
    id: "exec-roadmap-cost",
    pageNumber: 5,
    label: "24 Months, Cost and Proof",
    blocks: [
      { type: "visual", key: "roadmap" },
      { type: "p", text: "Overall discipline:" },
      { type: "flowchain", steps: ["Measure", "Configure", "Test", "Prove", "Adopt", "Retire"] },
      {
        type: "p",
        text: "0\u20136 months, Prove Rooms, runs a parallel discovery activity: define and sandbox the volunteer availability / service-match journey. Then, 7\u201312 months: select and pilot the simplest suitable volunteer scheduling approach against the sandbox requirements.",
      },
      {
        type: "p",
        text: "Volunteer contribution: approximately 2\u00bd days per month, with suppliers and operational owners undertaking routine implementation and operation.",
      },
      { type: "subhead", text: "Cost" },
      { type: "visual", key: "cost" },
      { type: "p", text: "The full report contains detailed cost assumptions and a resilience reserve." },
      { type: "subhead", text: "Proof" },
      {
        type: "list",
        variant: "grid",
        items: [
          "Did the experience improve?",
          "Did administration fall?",
          "Can ordinary users operate it?",
          "Can a deputy take over?",
          "What old process can now stop?",
          "Is the next investment justified?",
        ],
      },
    ],
  },
  {
    id: "exec-northstar",
    pageNumber: 6,
    label: "The North Star and Decision",
    blocks: [
      {
        type: "p",
        text: "The cumulative effect of the programme is that The Mill progressively develops a dynamic authorised profile of itself. Not a giant central database. Instead:",
      },
      { type: "quote", text: "Single authorised versions of important facts, presented through different purpose-appropriate views." },
      { type: "visual", key: "northstar" },
      { type: "p", text: "The future experience:" },
      { type: "quote", text: "The customer describes the need. The Mill presents the best authorised match." },
      {
        type: "p",
        text: "For example, somebody looking for an accessible room for a particular activity should increasingly be matched to the appropriate space without needing to understand The Mill's internal administration.",
      },
      { type: "p", text: "Why this matters for evidence: normal operations increasingly demonstrate:" },
      { type: "flowchain", steps: ["What happened", "What was learned", "What changed"] },
      {
        type: "p",
        text: "This supports trustee assurance, organisational learning and funder/KPI evidence without building a separate reporting bureaucracy.",
      },
      {
        type: "p",
        text: "KATLAS has explored the longer-term architecture internally under the working title CommunityHubCierge; it is a future-state design reference, not a recommendation or part of the 24-month implementation.",
      },
    ],
  },
];

// Same 12 recommendations as the full report, same numbering and meaning;
// wording shortened only where meaning is exactly unchanged.
export const EXEC_RECOMMENDATIONS = [
  "Approve this as an operating-model programme, not a CRM procurement exercise.",
  "Use the three rooms as the first bounded proof, with before/after measures.",
  "Pilot LemonBooking for room-commerce and a potential simpler web environment.",
  "After the room journey is stable, pilot the simplest volunteer scheduling solution that can match volunteer availability and capability to service needs, manage acceptance and replacement cover, and remain operable by ordinary Mill users. Use a broader community-management platform only where additional demonstrated requirements justify it.",
  "Pilot membermojo with one Network Organisation, preserving its ownership of members.",
  "Adopt distributed ownership and authority: named owners, authoritative systems, defined access and approval.",
  "Consolidate marketing around one authoritative Mill audience.",
  "Use tools such as Emergent for prototyping and training only, not as systems of record.",
  "Measure before and after every material change; stop changes that increase administrative burden.",
  "Make retirement part of implementation: retire the process each new capability replaces.",
  "Defer bespoke CommunityHub/AI orchestration until evidence shows commodity systems cannot solve the problem.",
  "Review the programme at 24 months against operational, financial and community outcomes, not the number of technologies installed.",
];
